import firebase from 'firebase';
import 'firebase/firestore';
import axios from 'axios';
import randomNumber from 'random-number';

const gen = randomNumber.generator({
  min: 100000,
  max: 999999,
  integer: true,
});

const actions = {
  // setup
  CREATE_DRAFT: (c, p) => createDraft(c, p),
  PURCHASE_DRAFT: (c, p) => purchaseDraft(c, p),
  SAVE_DRAFT: (c, p) => saveDraft(c, p),
  DELETE_DRAFT: (c, p) => deleteDraft(c, p),
  JOIN_DRAFT: (c, p) => joinDraft(c, p),
  SAVE_TEAMS: (c, p) => saveTeams(c, p),
  SAVE_POSITIONS: (c, p) => savePositions(c, p),
  SETUP_DRAFT: (c, p) => setupDraft(c, p),
  UPDATE_USER_DRAFTS: (c, p) => updateUserDrafts(c, p),
  SAVE_KEEPERS: (c, p) => saveKeepers(c, p),
  SAVE_TRADES: (c, p) => saveTrades(c, p),
  // live
  LOAD_DRAFT: (c, p) => loadDraft(c, p),
  LOAD_DRAFT_BY_CODE: (c, p) => loadDraftByCode(c, p),
  START_DRAFT: (c, p) => startDraft(c, p),
  PAUSE_DRAFT: (c, p) => pauseDraft(c, p),
  COMPLETE_DRAFT: (c, p) => completeDraft(c, p),
  DRAFT_PLAYER: (c, p) => draftPlayer(c, p),
  UNDO_PICK: (c, p) => undoPick(c, p),
};

const createDraft = ({ rootState, dispatch, commit }, {
  name,
  rounds,
  type,
  numTeams,
  baseRanking,
  duration,
}) => {
  const commissioner = rootState.auth.user.uid;
  const teams = [];

  for (let i = 0; i < numTeams; i += 1) {
    teams.push({
      name: `Team ${i + 1}`,
      id: i,
    });
  }

  teams[0].uid = commissioner;
  teams[0].email = rootState.auth.user.email;

  const created = firebase.firestore.FieldValue.serverTimestamp();
  const status = 'ready';

  const positions = [
    {
      position: 'QB',
      starters: 1,
      max: 3,
    },
    {
      position: 'RB',
      starters: 2,
      max: 8,
    },
    {
      position: 'WR',
      starters: 2,
      max: 8,
    },
    {
      position: 'FLEX',
      starters: 1,
    },
    {
      position: 'TE',
      starters: 1,
      max: 3,
    },
    {
      position: 'DST',
      starters: 1,
      max: 3,
    },
    {
      position: 'K',
      starters: 1,
      max: 3,
    },
  ];

  return new Promise((resolve, reject) => {
    const checkCode = (findCode) => {
      return firebase.firestore().collection('drafts')
        .where('code', '==', findCode)
        .limit(1)
        .get();
    };

    const generateCode = () => {
      const code = gen().toString();

      return checkCode(code)
        .then(({ docs }) => {
          if (docs && docs.length > 0) {
            return generateCode();
          }

          return code;
        });
    };

    generateCode()
      .then((code) => {
        const newDraft = {
          name,
          rounds,
          type: type.value,
          commissioner,
          teams,
          status,
          created,
          baseRanking,
          duration,
          positions,
          code,
          trades: [],
          keepers: {},
        };

        firebase.firestore().collection('drafts').add(newDraft)
          .then(({ id }) => {
            firebase.firestore().collection('users').doc(rootState.auth.user.uid).update({
              [`drafts.${id}`]: {
                id,
                name,
                teamName: teams[0].name,
                teams: teams.length,
                rounds,
                type: type.value,
                commissioner: true,
                status,
                joined: Date.now(),
              },
            })
              .then(() => {
                newDraft.id = id;
                commit('SET_DRAFT', newDraft);

                dispatch('SETUP_DRAFT')
                  .then(() => {
                    resolve(id);
                  });
              })
              .catch(reject);
          })
          .catch(reject);
      });
  });
};

const purchaseDraft = ({ state }, token) => {
  return axios.post(`${process.env.VUE_APP_FUNCTIONS_API}/draft/${state.draft.id}/purchase`, { token });
};

const saveDraft = ({ dispatch, commit }, {
  id,
  name,
  rounds,
  type,
  baseRanking,
  duration,
}) => {
  const update = {
    rounds,
    name,
    type: type.value,
    baseRanking,
    duration,
    status: 'ready',
  };

  commit('SET_DRAFT_BASE_RANKING', baseRanking);

  return new Promise((resolve, reject) => {
    firebase.firestore().collection('drafts').doc(id).update(update)
      .then(() => {
        dispatch('SETUP_DRAFT')
          .then(resolve)
          .catch(reject);
      })
      .catch(reject);
  });
};

const deleteDraft = ({ commit }, id) => {
  return new Promise((resolve, reject) => {
    firebase.firestore().collection('drafts').doc(id).delete()
      .then(() => {
        commit('REMOVE_DRAFT', id);
        resolve();
      })
      .catch(reject);
  });
};

const joinDraft = (context, { draftId, teamName }) => {
  return axios.post(`${process.env.VUE_APP_FUNCTIONS_API}/draft/${draftId}/join`, { teamName });
};

const saveTeams = ({
  state,
  commit,
  rootState,
  dispatch,
}, {
  teams,
  teamName,
  removedOwners,
}) => {
  return new Promise((resolve, reject) => {
    const draftRef = firebase.firestore().collection('drafts').doc(state.draft.id);

    return firebase.firestore().runTransaction((transaction) => {
      return transaction.get(draftRef)
        .then((draftDoc) => {
          if (teamName) {
            ({ teams } = draftDoc.data());

            const teamIndex = teams.findIndex((team) => {
              return team.uid === rootState.auth.user.uid;
            });

            if (teamIndex > -1) {
              teams[teamIndex].name = teamName;
            }
          }

          commit('SET_DRAFT_TEAMS', teams);

          const draftUpdateDoc = {
            teams,
          };

          transaction.update(draftRef, draftUpdateDoc);
        });
    })
      .then(() => {
        if (removedOwners && removedOwners.length) {
          const userDraftBatch = firebase.firestore().batch();

          removedOwners.forEach((uid) => {
            if (uid) {
              const userRef = firebase.firestore().collection('users').doc(uid);

              userDraftBatch.update(userRef, {
                [`drafts.${state.draft.id}`]: firebase.firestore.FieldValue.delete(),
              });
            }
          });

          userDraftBatch.commit();
        }

        dispatch('UPDATE_USER_DRAFTS');

        resolve();
      })
      .catch(reject);
  });
};

const savePositions = ({ state }, positions) => {
  const draftRef = firebase.firestore().collection('drafts').doc(state.draft.id);

  return firebase.firestore().runTransaction((transaction) => {
    return transaction.get(draftRef)
      .then(() => {
        transaction.update(draftRef, {
          positions,
          status: 'ready',
        });

        return positions;
      });
  });
};

const setupDraft = ({ state, dispatch, commit }) => {
  return new Promise((resolve, reject) => {
    const draftRef = firebase.firestore().collection('drafts').doc(state.draft.id);

    firebase.firestore().collection('baseRankings').doc(state.draft.baseRanking.id).get()
      .then((baseRankingDoc) => {
        const baseRanking = baseRankingDoc.data();

        return firebase.firestore().runTransaction((transaction) => {
          return transaction.get(draftRef)
            .then((draftDoc) => {
              const draft = draftDoc.data();

              let picks = [];

              for (let round = 1; round <= draft.rounds; round += 1) {
                const roundPicks = [];

                draft.teams.forEach((team) => {
                  const pick = {
                    t: team.id,
                  };

                  const keeperAtRound =
                    draft.keepers &&
                    draft.keepers[team.id] &&
                    draft.keepers[team.id].find(({ r }) => r === round);

                  if (keeperAtRound) {
                    pick.p = keeperAtRound.p;
                    pick.k = true;
                  }

                  if (draft.trades && draft.trades.length > 0) {
                    draft.trades.forEach((trade) => {
                      if (trade.t1 === team.id && trade.r1 === round) {
                        pick.t = trade.t2;
                      } else if (trade.t2 === team.id && trade.r2 === round) {
                        pick.t = trade.t1;
                      }
                    });
                  }

                  roundPicks.push(pick);
                });

                if (round % 2 === 0 && draft.type === 'snake') {
                  roundPicks.reverse();
                }

                picks = [...picks, ...roundPicks];
              }

              let currentPick = 1;

              while (picks[currentPick - 1].p) {
                currentPick += 1;
              }

              const draftSetup = {
                teams: draft.teams,
                picks,
                availablePlayers: baseRanking.rankings,
                currentPick,
                status: 'ready',
                lastDrafted: null,
              };

              commit('SET_DRAFT_SETUP', draftSetup);

              transaction.update(draftRef, draftSetup);
            });
        })
          .then(() => {
            dispatch('UPDATE_USER_DRAFTS');
            resolve();
          })
          .catch(reject);
      });
  });
};

const updateUserDrafts = ({ state }) => {
  const batch = firebase.firestore().batch();

  state.draft.teams.forEach(({ uid, name }) => {
    if (uid) {
      const userRef = firebase.firestore().collection('users').doc(uid);
      const draftUpdate = {
        id: state.draft.id,
        name: state.draft.name,
        teamName: name,
        teams: state.draft.teams.length,
        rounds: state.draft.rounds,
        type: state.draft.type,
        status: state.draft.status,
      };

      if (state.draft.commissioner === uid) {
        draftUpdate.commissioner = true;
      }

      batch.update(userRef, {
        [`drafts.${state.draft.id}`]: draftUpdate,
      });
    }
  });

  return batch.commit();
};

const saveKeepers = ({ state, dispatch }, { teamId, keepers }) => {
  const draftRef = firebase.firestore().collection('drafts').doc(state.draft.id);

  return new Promise((resolve) => {
    return firebase.firestore().runTransaction((transaction) => {
      return transaction.get(draftRef)
        .then((draftDoc) => {
          const savedKeepers = draftDoc.data().keepers || {};

          savedKeepers[teamId] = keepers;

          transaction.update(draftRef, {
            keepers: savedKeepers,
          });

          return savedKeepers;
        });
    })
      .then(() => {
        dispatch('SETUP_DRAFT');
        resolve();
      });
  });
};

const saveTrades = ({ state, dispatch }, trades) => {
  const draftRef = firebase.firestore().collection('drafts').doc(state.draft.id);

  return new Promise((resolve) => {
    return firebase.firestore().runTransaction((transaction) => {
      return transaction.get(draftRef)
        .then(() => {
          transaction.update(draftRef, {
            trades,
          });

          return trades;
        });
    })
      .then(() => {
        dispatch('SETUP_DRAFT');
        resolve();
      });
  });
};

let draftDocListener;

const loadDraft = ({ commit, dispatch }, id) => {
  return new Promise((resolve, reject) => {
    dispatch('lists/LOAD_PLAYERS', {}, { root: true })
      .then(() => {
        dispatch('lists/LOAD_TEAMS', {}, { root: true })
          .then(() => {
            draftDocListener = firebase.firestore().collection('drafts').doc(id)
              .onSnapshot((draftDoc) => {
                if (draftDoc.exists) {
                  const draft = draftDoc.data();
                  draft.id = draftDoc.id;

                  commit('SET_DRAFT', draft);
                  resolve();
                } else {
                  draftDocListener();
                  reject();
                }
              });
          });
      });
  });
};

const loadDraftByCode = ({ commit }, code) => {
  return new Promise((resolve, reject) => {
    firebase.firestore().collection('drafts')
      .where('code', '==', code)
      .limit(1)
      .get()
      .then(({ docs }) => {
        if (!docs || docs.length === 0) {
          reject();
          return;
        }

        const draft = docs[0].data();
        draft.id = docs[0].id;

        commit('SET_DRAFT', draft);
        resolve();
      })
      .catch(reject);
  });
};

const startDraft = ({ state }) => {
  return axios.post(`${process.env.VUE_APP_FUNCTIONS_API}/draft/${state.draft.id}/start`);
};

const pauseDraft = ({ state, dispatch }) => {
  const pauseTime = Math.max(0, state.draft.endTime - Date.now());

  firebase.firestore().collection('drafts').doc(state.draft.id).update({
    status: 'paused',
    pauseTime,
  })
    .then(() => {
      dispatch('UPDATE_USER_DRAFTS');
    });
};

const completeDraft = ({ state, dispatch }) => {
  firebase.firestore().collection('drafts').doc(state.draft.id).update({
    status: 'completed',
  })
    .then(() => {
      dispatch('UPDATE_USER_DRAFTS');
    });
};

const draftPlayer = ({ state, commit }, playerId) => {
  commit('DRAFT_PLAYER', playerId);

  return axios.post(`${process.env.VUE_APP_FUNCTIONS_API}/draft/${state.draft.id}/pick`, { id: playerId });
};

const undoPick = ({ state }) => {
  const draftRef = firebase.firestore().collection('drafts').doc(state.draft.id);

  firebase.firestore().runTransaction((transaction) => {
    return transaction.get(draftRef)
      .then((draftDoc) => {
        const draft = draftDoc.data();

        if (draft.currentPick >= 2) {
          // Add buffer of another 5 seconds to get things caught up
          const endTime = Date.now() + (draft.duration * 1000) + 5000;

          let pick = draft.currentPick - 2;

          while (pick > 0 && draft.picks[pick].k) {
            pick -= 1;
          }

          if (!draft.picks[pick].k) {
            delete draft.picks[pick].p;
            draft.currentPick -= 1;
          }

          transaction.update(draftRef, {
            picks: draft.picks,
            currentPick: draft.currentPick,
            lastDrafted: null,
            endTime,
          });
        }
      });
  });
};

export default actions;
